import React, { useState } from "react";
import SimulationDataGeneratorAPI from "../services/SimulationDataGeneratorAPI";
import LoadingSpinner from "../common/LoadingSpinner";

const AVAILABLE_CONTEXTS = [
  "Emergencies",
  "Financial",
  "Regional",
  "Job",
  "FreeOffer",
  "Weather",
  "Recycling",
  "EverydayLife",
  "General",
  "Event",
];

/**
 * Configuration form for generating simulation messages via AI.
 */
const GeneratorForm = ({ onGenerationComplete, onSwitchToApproval }) => {
  const [channel, setChannel] = useState("EMAIL");
  const [count, setCount] = useState(10);
  const [minLevel, setMinLevel] = useState(1);
  const [maxLevel, setMaxLevel] = useState(10);
  const [selectedContexts, setSelectedContexts] = useState([]);
  const [customContext, setCustomContext] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [successResult, setSuccessResult] = useState(null);
  const [error, setError] = useState(null);
  const [batchProgress, setBatchProgress] = useState(null); // { current, total, generated }

  // EMAIL includes large HTML payloads — 5 per batch is fine.
  // SMS / VOICE / SOCIAL_MEDIA prompts can be more open-ended; keep batches smaller
  // to stay well under the 29 s API Gateway timeout.
  const BATCH_SIZE = channel === "EMAIL" ? 5 : 3;

  const toggleContext = (ctx) => {
    setSelectedContexts((prev) =>
      prev.includes(ctx) ? prev.filter((c) => c !== ctx) : [...prev, ctx],
    );
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    setSuccessResult(null);
    setBatchProgress(null);

    try {
      const allContexts = [
        ...selectedContexts,
        ...customContext
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      ];

      const totalCount = parseInt(count) || 10;
      const batchSize = channel === "EMAIL" ? 5 : 3;
      const baseRequest = {
        communicationChannel: channel,
        sophisticationLevelMin: parseInt(minLevel) || 1,
        sophisticationLevelMax: parseInt(maxLevel) || 10,
        contexts: allContexts.length > 0 ? allContexts : null,
      };

      // Split into batches to avoid AWS API Gateway 29s timeout.
      // EMAIL uses batches of 5 (HTML-heavy but well-tested).
      // SMS / VOICE / SOCIAL_MEDIA use batches of 3 to stay under the 29 s limit.
      const batches = [];
      let remaining = totalCount;
      while (remaining > 0) {
        batches.push(Math.min(remaining, batchSize));
        remaining -= batchSize;
      }

      let combinedMessages = [];
      let lastBatchId = null;
      let lastChannel = channel;

      for (let i = 0; i < batches.length; i++) {
        setBatchProgress({ current: i + 1, total: batches.length, generated: combinedMessages.length });
        const batchRequest = { ...baseRequest, count: batches[i] };
        const result = await SimulationDataGeneratorAPI.generateMessages(batchRequest);
        combinedMessages = combinedMessages.concat(result.messages || []);
        lastBatchId = result.batchId;
        lastChannel = result.channel;
      }

      const aggregatedResult = {
        messages: combinedMessages,
        totalGenerated: combinedMessages.length,
        channel: lastChannel,
        batchId: lastBatchId,
      };

      setSuccessResult(aggregatedResult);
      if (onGenerationComplete) onGenerationComplete();
    } catch (err) {
      console.error("Generation error:", err);
      setError(
        err.message ||
          err.error ||
          "Failed to generate messages. Please try again.",
      );
    } finally {
      setIsGenerating(false);
      setBatchProgress(null);
    }
  };

  if (isGenerating) {
    const batchMsg = batchProgress
      ? `Batch ${batchProgress.current} of ${batchProgress.total} — ${batchProgress.generated} messages generated so far...`
      : `Generating ${count} ${channel.toLowerCase()} simulation messages...`;
    return (
      <div className="generator-form">
        <div className="generation-loading">
          <LoadingSpinner message={batchMsg} />
          {batchProgress && batchProgress.total > 1 && (
            <div style={{ width: "100%", maxWidth: 360, margin: "12px auto 0" }}>
              <div style={{ background: "#e2e8f0", borderRadius: 6, height: 6 }}>
                <div
                  style={{
                    background: "#3b82f6",
                    borderRadius: 6,
                    height: 6,
                    width: `${Math.round((batchProgress.current / batchProgress.total) * 100)}%`,
                    transition: "width 0.4s ease",
                  }}
                />
              </div>
              <p style={{ fontSize: "0.78rem", color: "#94a3b8", marginTop: 6, textAlign: "center" }}>
                {Math.round((batchProgress.current / batchProgress.total) * 100)}% complete
              </p>
            </div>
          )}
          <p style={{ fontSize: "0.82rem", color: "#94a3b8", marginTop: 8 }}>
            Requests are sent in small batches to ensure reliability.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="generator-form">
      <h3>Generate Simulation Messages</h3>

      {/* Channel Selection */}
      <div className="form-row">
        <div className="form-group">
          <label>Communication Channel</label>
          <div className="channel-toggle">
            <button
              className={channel === "EMAIL" ? "active" : ""}
              onClick={() => setChannel("EMAIL")}
            >
              Email
            </button>
            <button
              className={channel === "SMS" ? "active" : ""}
              onClick={() => setChannel("SMS")}
            >
              SMS
            </button>
            <button
              className={channel === "VOICE" ? "active" : ""}
              onClick={() => setChannel("VOICE")}
            >
              Voice
            </button>
            <button
              className={channel === "SOCIAL_MEDIA" ? "active" : ""}
              onClick={() => setChannel("SOCIAL_MEDIA")}
            >
              Social Media
            </button>
          </div>
        </div>

        <div className="form-group">
          <label>Number of Messages (1-50)</label>
          <input
            type="number"
            min="1"
            max="50"
            value={count}
            onChange={(e) => setCount(e.target.value)}
          />
        </div>
      </div>

      {/* Sophistication Range */}
      <div className="form-row">
        <div className="form-group">
          <label>Min Sophistication Level (1-10)</label>
          <input
            type="number"
            min="1"
            max="10"
            value={minLevel}
            onChange={(e) => setMinLevel(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Max Sophistication Level (1-10)</label>
          <input
            type="number"
            min="1"
            max="10"
            value={maxLevel}
            onChange={(e) => setMaxLevel(e.target.value)}
          />
        </div>
      </div>

      {/* Context Selection */}
      <div className="form-group" style={{ marginBottom: "20px" }}>
        <label>Contexts (optional — leave blank for all)</label>
        <div className="context-checkboxes">
          {AVAILABLE_CONTEXTS.map((ctx) => (
            <label
              key={ctx}
              className={`context-checkbox ${selectedContexts.includes(ctx) ? "selected" : ""}`}
            >
              <input
                type="checkbox"
                checked={selectedContexts.includes(ctx)}
                onChange={() => toggleContext(ctx)}
              />
              {ctx}
            </label>
          ))}
        </div>
        <input
          type="text"
          className="custom-context-input"
          placeholder="Custom context (comma-separated, e.g. Insurance, Healthcare)"
          value={customContext}
          onChange={(e) => setCustomContext(e.target.value)}
        />
      </div>

      {/* Generate Button */}
      <button
        className="generate-btn"
        onClick={handleGenerate}
        disabled={parseInt(minLevel) > parseInt(maxLevel)}
      >
        Generate {count} {channel} Messages
      </button>

      {/* Validation Warning */}
      {parseInt(minLevel) > parseInt(maxLevel) && (
        <div className="error-message">
          Min sophistication level cannot exceed max level.
        </div>
      )}

      {/* Error Display */}
      {error && <div className="error-message">{error}</div>}

      {/* Success Result */}
      {successResult && (
        <div className="generation-success">
          <span className="success-icon">&#10003;</span>
          <span className="success-text">
            {successResult.totalGenerated}{" "}
            {successResult.channel?.toLowerCase()} messages generated and staged
            for approval.
          </span>
          <button className="review-link" onClick={onSwitchToApproval}>
            Review in Approval Dashboard &rarr;
          </button>
        </div>
      )}
    </div>
  );
};

export default GeneratorForm;
